import React, { useState } from 'react';
import { Icon } from '../../Icon';
import { parseBrazilianNumber } from '../../../utils/formatUtils';
import { FormField, ResultCard, EducationalBlock } from '../finance/shared';

const CaloriesCalculator = ({ onResultChange }: { onResultChange: (data: { label: string, value: string }[]) => void }) => {
  const [mode, setMode] = useState<'maintain' | 'lose' | 'gain'>('maintain');
  const [gender, setGender] = useState<'male' | 'female'>('male');
  const [age, setAge] = useState('30');
  const [height, setHeight] = useState('170');
  const [weight, setWeight] = useState('70,00');
  const [activityLevel, setActivityLevel] = useState<'sedentary' | 'light' | 'moderate' | 'active'>('sedentary');
  const [goal, setGoal] = useState<'weight' | 'percentage'>('weight'); // Mantido do original embora não usado diretamente
  const [result, setResult] = useState<any>(null);

  const calculate = () => {
    const w = parseFloat(parseBrazilianNumber(weight)) || 0;
    const h = parseFloat(height) || 0;
    const a = parseFloat(age) || 0;
    const activity = activityLevel === 'sedentary' ? 1.2 : activityLevel === 'light' ? 1.375 : activityLevel === 'moderate' ? 1.55 : 1.725;

    // Cálculo do TMB (Harris-Benedict)
    let tmb = 0;
    if (gender === 'male') {
      tmb = 88.362 + (13.397 * w) + (4.799 * h) - (5.677 * a);
    } else {
      tmb = 447.593 + (9.247 * w) + (3.098 * h) - (4.330 * a);
    }

    // Cálculo das calorias baseado no objetivo
    let calories = 0;
    if (mode === 'maintain') {
      calories = tmb * 1; // Base maintain
    } else if (mode === 'lose') {
      calories = tmb * 0.8;
    } else if (mode === 'gain') {
      calories = tmb * 1.2;
    }

    const adjustedCalories = calories * activity;
    const protein = (adjustedCalories * 0.3) / 4;
    const carbs = (adjustedCalories * 0.5) / 4;
    const fats = (adjustedCalories * 0.2) / 9;

    const bmi = w / ((h / 100) * (h / 100));

    const res = {
      tmb: tmb.toFixed(0),
      calories: adjustedCalories.toFixed(0),
      protein: protein.toFixed(1),
      carbs: carbs.toFixed(1),
      fats: fats.toFixed(1),
      bmi: bmi.toFixed(1),
      mode,
      gender,
      age,
      height,
      weight: w,
      activityLevel
    };
    setResult(res);

    onResultChange([
      { label: 'Objetivo', value: mode === 'maintain' ? 'Manter Peso' : mode === 'lose' ? 'Emagrecer' : 'Hipertrofia' },
      { label: 'Ingestão Diária', value: `${res.calories} kcal` },
      { label: 'Proteínas', value: `${res.protein} g` },
      { label: 'Carboidratos', value: `${res.carbs} g` },
      { label: 'Gorduras', value: `${res.fats} g` }
    ]);
  };

  return (
    <div className="space-y-8">
      <div className="bg-theme-result p-8 rounded-3xl shadow-lg border border-theme-border">
        {/* Seletor de objetivo */}
        <div className="mb-6">
          <FormField
            label="Objetivo"
            value={mode}
            onChange={setMode}
            type="select"
            required
            options={[
              { value: 'maintain', label: 'Manter Peso' },
              { value: 'lose', label: 'Emagrecer' },
              { value: 'gain', label: 'Hipertrofia' }
            ]}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <FormField
            label="Gênero"
            value={gender}
            onChange={setGender}
            type="select"
            required
            options={[
              { value: 'male', label: 'Masculino' },
              { value: 'female', label: 'Feminino' }
            ]}
          />
          <FormField
            label="Idade"
            placeholder="Ex: 30"
            value={age}
            onChange={setAge}
            required
            isInteger
            type="text"
          />
          <FormField
            label="Altura (cm)"
            placeholder="Ex: 170"
            value={height}
            onChange={setHeight}
            required
            isInteger
            type="text"
          />
          <FormField
            label="Peso (kg)"
            placeholder="Ex: 70,00"
            value={weight}
            onChange={setWeight}
            required
            isNumeric
            type="text"
          />
          <FormField
            label="Nível de Atividade"
            value={activityLevel}
            onChange={setActivityLevel}
            type="select"
            required
            options={[
              { value: 'sedentary', label: 'Sedentário (1.2)' },
              { value: 'light', label: 'Leve (1.375)' },
              { value: 'moderate', label: 'Moderado (1.55)' },
              { value: 'active', label: 'Ativo (1.725)' }
            ]}
          />
        </div>

        <div className="flex justify-center mb-8">
          <button
            onClick={calculate}
            className="px-12 py-4 bg-accent-gradient text-white rounded-xl font-bold hover:shadow-accent-hover hover:scale-[1.02] transition-all flex items-center justify-center gap-2"
          >
            <Icon name="play" className="w-5 h-5" />
            Calcular Calorias e Macros
          </button>
        </div>

        {result && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <ResultCard
              title="Taxa Metabólica (TMB)"
              value={`${result.tmb} kcal/dia`}
              description="Basal sem atividade"
              color="blue"
            />
            <ResultCard
              title="Calorias Diárias"
              value={`${result.calories} kcal`}
              description="Ajustadas por atividade"
              color="green"
            />
            <ResultCard
              title="Proteínas"
              value={`${result.protein}g`}
              description="30% do total"
              color="purple"
            />
            <ResultCard
              title="Carboidratos"
              value={`${result.carbs}g`}
              description="50% do total"
              color="orange"
            />
            <ResultCard
              title="Gorduras"
              value={`${result.fats}g`}
              description="20% do total"
              color="red"
            />
            <ResultCard
              title="IMC"
              value={result.bmi}
              description="Índice de Massa Corporal"
              color="slate"
            />
          </div>
        )}
      </div>

      <EducationalBlock
        title="Como Funciona a Calculadora Calórica"
        tips={[
          'TMB (Taxa Metabólica Basal): Calorias que seu corpo gasta em repouso',
          'Para emagrecer: Consuma menos calorias que queima (déficit calórico)',
          'Para hipertrofia: Consuma mais calorias que queima (superávit calórico)',
          'Proteínas: 1g de proteína = 4 calorias',
          'Carboidratos: 1g de carboidrato = 4 calorias',
          'Gorduras: 1g de gordura = 9 calorias',
          'Atividade física aumenta suas necessidades calóricas',
          'O IMC (Índice de Massa Corporal) é uma medida de referência universal'
        ]}
      />
    </div>
  );
};

export default CaloriesCalculator;
